import { encodeFunctionData, parseEventLogs, type Address, type Hex, type Log, type PublicClient } from 'viem'
import { getRobinhoodPublicClient } from '@/lib/chain/publicClient'
import { curveFactoryAbi, curveLaunchAbi, penguinHookAbi } from '@/lib/launchpad/abi'
import { BPS_DENOM, GRADUATION_QUOTE_WEI, TEST_LAUNCH_FEE_WEI, TRADE_FEE_BPS } from '@/lib/launchpad/constants'
import { getCurveFactoryAddress, getCurveFactoryAddressV2, getPenguinHookAddress, isNonZeroAddress } from '@/lib/launchpad/contracts'
import { buyOnCurve } from '@/lib/launchpad/curveMath'
import type { LaunchState } from '@/lib/launchpad/types'
import { PRESALE_ACTION_GAS } from '@/lib/presale/tx'

/**
 * LOCK: curve buy/sell/claim gas — do not change without updating
 * docs/presale-wallet-workflow.md and src/lib/launchpad/tx.test.ts.
 * Normal trades MUST equal working contribute (500k). Oversized gas (3M) made
 * MetaMask fail its confirm-sheet eth_call even when the tx itself was valid.
 */
export const CURVE_TRADE_GAS = PRESALE_ACTION_GAS
/** Only when this buy would graduate into Uniswap v4. Still well below a 3M gas wall. */
export const CURVE_GRADUATE_GAS = 1_500_000n
/** @deprecated Use CURVE_TRADE_GAS / CURVE_GRADUATE_GAS. Kept so existing imports keep compiling. */
export const CURVE_ACTION_GAS = CURVE_TRADE_GAS

type CurveWalletRequest = {
  address: Address
  data: Hex
  value?: bigint
  gas: bigint
  maxFeePerGas: bigint
  maxPriorityFeePerGas: bigint
}

/**
 * LOCK: curve buy/sell/claim wallet requests MUST always carry explicit gas
 * AND EIP-1559 fee fields (maxFeePerGas / maxPriorityFeePerGas). Without
 * these, wallets fall back to their own eth_call gas/fee estimation before
 * showing the confirm sheet, and Robinhood Chain's eth_call simulation is
 * unreliable — this is what made on-chain quick-launch buy/sell silently
 * fail or never open the wallet confirm sheet. Mirrors the working
 * presale contribute/withdraw/settle request shape. Do not strip these.
 */
export const CURVE_MAX_FEE_PER_GAS = 10_000_000_000n // 10 gwei
export const CURVE_MAX_PRIORITY_FEE_PER_GAS = 2_000_000_000n // 2 gwei

function curveWalletRequest(address: Address, data: Hex, gas: bigint, value?: bigint): CurveWalletRequest {
  return {
    address,
    data,
    value,
    gas,
    maxFeePerGas: CURVE_MAX_FEE_PER_GAS,
    maxPriorityFeePerGas: CURVE_MAX_PRIORITY_FEE_PER_GAS,
  }
}

export function encodeCreateLaunchCalldata(name: string, ticker: string, imageUrl: string): Hex {
  return encodeFunctionData({
    abi: curveFactoryAbi,
    functionName: 'createLaunch',
    args: [name, ticker, imageUrl],
  })
}

export function encodeBuyCalldata(): Hex {
  return encodeFunctionData({
    abi: curveLaunchAbi,
    functionName: 'buy',
  })
}

export function encodeSellCalldata(tokenIn: bigint): Hex {
  return encodeFunctionData({
    abi: curveLaunchAbi,
    functionName: 'sell',
    args: [tokenIn],
  })
}

export function encodeClaimCreatorFeesCalldata(): Hex {
  return encodeFunctionData({
    abi: curveLaunchAbi,
    functionName: 'claimCreatorFees',
  })
}

export function encodeClaimHookCreatorFeesCalldata(token: Address): Hex {
  return encodeFunctionData({
    abi: penguinHookAbi,
    functionName: 'claimCreatorFees',
    args: [token],
  })
}

/**
 * LOCK: readCurveLaunchFee() backs the create-launch flow only, so it always
 * reads from the NEW CurveFactory (V2). Existing launches already indexed
 * from the original factory are unaffected — this fee is only used when
 * sending a brand-new createLaunch transaction.
 */
export async function readCurveLaunchFee(client?: PublicClient): Promise<bigint> {
  const factory = getCurveFactoryAddressV2()
  if (!factory)
    throw new Error('Curve factory address is not set')
  const reader = client ?? getRobinhoodPublicClient()
  try {
    return await reader.readContract({
      address: factory,
      abi: curveFactoryAbi,
      functionName: 'launchFee',
    })
  }
  catch {
    // Live factory still has production=false, so launchFee() is WAD/1_000_000.
    return TEST_LAUNCH_FEE_WEI
  }
}

/**
 * Live CurveLaunch.graduationQuote(), then factory.graduationQuote(), then
 * the 4.2 ETH fallback. `factory` defaults to the original CurveFactory for
 * general-purpose reads; the create-launch flow passes the new CurveFactory
 * (V2) explicitly since that is where the new launch will be deployed.
 */
export async function readCurveGraduationQuote(
  launchAddress?: Address,
  client?: PublicClient,
  factory: Address | undefined = getCurveFactoryAddress(),
): Promise<bigint> {
  const reader = client ?? getRobinhoodPublicClient()
  if (isNonZeroAddress(launchAddress)) {
    try {
      const quote = await reader.readContract({
        address: launchAddress,
        abi: curveLaunchAbi,
        functionName: 'graduationQuote',
      })
      if (quote > 0n)
        return quote
    }
    catch {
      // Older launches may not expose graduationQuote(); try the factory next.
    }
  }
  if (factory) {
    try {
      const quote = await reader.readContract({
        address: factory,
        abi: curveFactoryAbi,
        functionName: 'graduationQuote',
      })
      if (quote > 0n)
        return quote
    }
    catch {
      // Factory view missing — keep protocol fallback.
    }
  }
  return GRADUATION_QUOTE_WEI
}

/** LOCK: all NEW createLaunch calls target the new CurveFactory (V2). Existing launches stay on the original factory. */
export function buildCreateLaunchTx(name: string, ticker: string, imageUrl: string, feeWei: bigint) {
  const factory = getCurveFactoryAddressV2()
  if (!factory)
    throw new Error('Curve factory address is not set')
  return {
    address: factory,
    abi: curveFactoryAbi,
    functionName: 'createLaunch' as const,
    args: [name, ticker, imageUrl] as const,
    value: feeWei,
  }
}

export function buildBuyTx(launch: Address, quoteIn: bigint) {
  return {
    address: launch,
    abi: curveLaunchAbi,
    functionName: 'buy' as const,
    args: [] as const,
    value: quoteIn,
  }
}

export function wouldCurveBuyGraduate(launch: LaunchState, quoteIn: bigint, nowMs = Date.now()): boolean {
  if (launch.status === 'graduated' || quoteIn <= 0n)
    return false
  try {
    return buyOnCurve(launch, quoteIn, nowMs).result.graduated
  }
  catch {
    return false
  }
}

export function curveBuyGasForLaunch(launch: LaunchState, quoteIn: bigint, nowMs = Date.now()): bigint {
  return wouldCurveBuyGraduate(launch, quoteIn, nowMs) ? CURVE_GRADUATE_GAS : CURVE_TRADE_GAS
}

export function buildBuyWalletRequest(launch: Address, quoteIn: bigint, gas: bigint = CURVE_TRADE_GAS) {
  return curveWalletRequest(launch, encodeBuyCalldata(), gas, quoteIn)
}

export function buildSellTx(launch: Address, tokenIn: bigint) {
  return {
    address: launch,
    abi: curveLaunchAbi,
    functionName: 'sell' as const,
    args: [tokenIn] as const,
  }
}

export function buildSellWalletRequest(launch: Address, tokenIn: bigint) {
  return curveWalletRequest(launch, encodeSellCalldata(tokenIn), CURVE_TRADE_GAS)
}

export function buildClaimCreatorFeesTx(launch: Address) {
  return {
    address: launch,
    abi: curveLaunchAbi,
    functionName: 'claimCreatorFees' as const,
    args: [] as const,
  }
}

export function buildClaimCreatorFeesWalletRequest(launch: Address) {
  return curveWalletRequest(launch, encodeClaimCreatorFeesCalldata(), CURVE_TRADE_GAS)
}

export function buildClaimHookCreatorFeesTx(token: Address) {
  return {
    address: getPenguinHookAddress(),
    abi: penguinHookAbi,
    functionName: 'claimCreatorFees' as const,
    args: [token] as const,
  }
}

export function buildClaimHookCreatorFeesWalletRequest(token: Address) {
  return curveWalletRequest(getPenguinHookAddress(), encodeClaimHookCreatorFeesCalldata(token), CURVE_TRADE_GAS)
}

export type LiveCreatorFees = {
  launchFees: bigint
  hookFees: bigint
  leftover: bigint
  token?: Address
}

export function totalLiveCreatorFees(fees: LiveCreatorFees): bigint {
  return (fees.launchFees ?? 0n) + (fees.hookFees ?? 0n) + (fees.leftover ?? 0n)
}

function uniqueAddresses(...values: Array<string | undefined | null>): Address[] {
  const next: Address[] = []
  for (const value of values) {
    if (!isNonZeroAddress(value))
      continue
    if (next.some(item => item.toLowerCase() === value.toLowerCase()))
      continue
    next.push(value)
  }
  return next
}

export async function readLiveCreatorFees(
  launch: LaunchState,
  client?: PublicClient,
): Promise<LiveCreatorFees> {
  const reader = client ?? getRobinhoodPublicClient()
  const launchAddress = isNonZeroAddress(launch.contractAddress) ? launch.contractAddress : undefined
  let token = isNonZeroAddress(launch.tokenAddress) ? launch.tokenAddress : undefined
  let launchFees = 0n
  let hookFees = 0n
  let leftover = 0n
  let pair: Address | undefined

  if (launchAddress) {
    const [onLaunch, onToken, onPair, launchBalance] = await Promise.all([
      reader.readContract({
        address: launchAddress,
        abi: curveLaunchAbi,
        functionName: 'creatorFeesAccrued',
      }).catch(() => 0n),
      reader.readContract({
        address: launchAddress,
        abi: curveLaunchAbi,
        functionName: 'token',
      }).catch(() => undefined),
      reader.readContract({
        address: launchAddress,
        abi: curveLaunchAbi,
        functionName: 'pair',
      }).catch(() => undefined),
      reader.getBalance({ address: launchAddress }).catch(() => 0n),
    ])
    launchFees = onLaunch
    if (isNonZeroAddress(onToken))
      token = onToken
    if (isNonZeroAddress(onPair))
      pair = onPair
    // CurveLaunch is the ERC-20 in some live listings, so token() can be 0x0.
    if (!token)
      token = launchAddress
    if (launch.status === 'graduated' && launchBalance > launchFees)
      leftover = launchBalance - launchFees
  }

  const seed = uniqueAddresses(token, launchAddress, pair)
  const hookAddress = getPenguinHookAddress()
  const seen = new Set(seed.map(value => value.toLowerCase()))
  const hookReads: Array<{ candidate: Address, accrued: bigint, registered: boolean }> = []

  async function readHook(candidate: Address) {
    const [accrued, listing] = await Promise.all([
      reader.readContract({
        address: hookAddress,
        abi: penguinHookAbi,
        functionName: 'creatorFeesAccrued',
        args: [candidate],
      }).catch(() => 0n),
      reader.readContract({
        address: hookAddress,
        abi: penguinHookAbi,
        functionName: 'listings',
        args: [candidate],
      }).catch(() => undefined),
    ])
    const listingCreator = listing?.[0]
    const listingAddress = listing?.[1]
    const registered = Boolean(listing?.[2]) || isNonZeroAddress(listingCreator)
    hookReads.push({ candidate, accrued, registered })
    if (isNonZeroAddress(listingAddress) && !seen.has(listingAddress.toLowerCase())) {
      seen.add(listingAddress.toLowerCase())
      await readHook(listingAddress)
    }
  }

  await Promise.all(seed.map(readHook))

  if (hookReads.length > 0) {
    const best = hookReads.reduce((winner, row) => {
      if (row.accrued > winner.accrued)
        return row
      if (row.accrued === winner.accrued && row.registered && !winner.registered)
        return row
      return winner
    }, hookReads[0]!)
    hookFees = best.accrued
    token = best.candidate
  }

  return { launchFees, hookFees, leftover, token }
}

export function parseLaunchCreatedAddress(logs: readonly Log[]): Address | undefined {
  const parsed = parseEventLogs({
    abi: curveFactoryAbi,
    eventName: 'LaunchCreated',
    logs,
  })
  return parsed[0]?.args?.launch
}
